import { secureRoute } from '@uag/react-core';
import { t } from 'i18next';
import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router';

import { isLoginEnabled } from './authMode';
import { CatalogPage } from './catalog/CatalogPage';
import { DocumentViewerPage } from './catalog/DocumentViewerPage';
import { SystemDetailPage } from './catalog/SystemDetailPage';
import { Frame } from './Frame';
import { LandingPage } from './LandingPage';
import { NotFound } from './NotFound';
import { Unauthorized } from './Unauthorized';

const SecureFrame = secureRoute(Frame);

// Code splitting: everything reachable from the landing page and the catalog list stays in the
// initial bundle (that is the path every session takes), the rest is fetched when its route is
// first entered. React Router's own `lazy` does that without a Suspense wrapper - it resolves the
// module before rendering the route.
//
// The big one is the PDF viewer: `pdfjs-dist` is roughly a megabyte of JavaScript that most
// sessions never open, and it used to be downloaded and parsed on every first load. The article
// detail (with its tabs, compare grouping and lightbox) and the Merkliste follow the same rule.
//
// DocumentViewerPage and SystemDetailPage stay eager on purpose: both are three-line redirects, so
// splitting them would trade a network round trip for nothing.
const lazyArticleDetail = async () => ({ Component: (await import('./catalog/ArticleDetailPage')).ArticleDetailPage });
const lazyDocumentOverlay = async () => ({ Component: (await import('./catalog/DocumentOverlayPage')).DocumentOverlayPage });
const lazyWishlist = async () => ({ Component: (await import('./catalog/WishlistPage')).WishlistPage });

// See authMode.ts: whenever login is disabled (by default that's the static catalog export, which
// runs with no backend and no internet), the route-level auth guard - which would otherwise
// redirect to a login the offline build can neither reach nor return from - is skipped too, in
// favour of the plain, unguarded Frame.
const RootFrame = isLoginEnabled ? SecureFrame : Frame;

export const AppRoutes = () => {
    return (
        <Route Component={RootFrame} path="/">
            <Route Component={LandingPage} handle={{ crumb: t('home') }} path="/" />
            {/* Article detail and PDF viewer are CHILDREN of /catalog on purpose: React Router then
                keeps CatalogPage mounted (with its scroll position, filters and loaded data) and
                passes the child through `useOutlet()`, which CatalogPage renders as an overlay over
                the list - see components/DetailOverlay.tsx. A direct link to either URL still
                works; it just renders the list underneath as well. */}
            <Route Component={CatalogPage} handle={{ crumb: t('catalogTitle') }} path="/catalog">
                <Route handle={{ crumb: t('catalogTitle') }} lazy={lazyArticleDetail} path="article/:artNr" />
                <Route handle={{ crumb: t('catalogTitle') }} lazy={lazyDocumentOverlay} path="document/:assetId" />
            </Route>
            {/* Same overlay pattern as /catalog above: an article opened from the Merkliste keeps
                the Merkliste behind it. The relative links in ProductGrid/DocumentSearchResults
                resolve to whichever of the two parents is currently rendering them. */}
            <Route handle={{ crumb: t('catalogTitle') }} lazy={lazyWishlist} path="/catalog/wishlist">
                <Route handle={{ crumb: t('catalogTitle') }} lazy={lazyArticleDetail} path="article/:artNr" />
                <Route handle={{ crumb: t('catalogTitle') }} lazy={lazyDocumentOverlay} path="document/:assetId" />
            </Route>
            <Route Component={SystemDetailPage} handle={{ crumb: t('catalogTitle') }} path="/catalog/system/:nodeId" />
            {/* The old full-page viewer URL - kept alive for existing links/bookmarks, but it only
                redirects into the overlay above now (same reason SystemDetailPage is a redirect). */}
            <Route
                Component={DocumentViewerPage}
                handle={{ crumb: t('catalogTitle') }}
                path="/catalog/article/:artNr/document/:assetId"
            />
            <Route Component={Unauthorized} path="/unauthorized" />
            <Route Component={NotFound} path="*" />
        </Route>
    );
};

export const router = createBrowserRouter(createRoutesFromElements(AppRoutes()));
